import { DateUnit, RelativeDateDirection, type RelativeDate } from '@reach/shared-types/conditions';
import React from 'react';
import styles from './SegmentVisualBuilder.module.css';

interface RelativeDateBuilderProps {
  value: RelativeDate | null;
  onChange: (value: RelativeDate) => void;
  disabled?: boolean;
}

/**
 * UI for relative date inputs such as "18 years before today".
 */
export const RelativeDateBuilder: React.FC<RelativeDateBuilderProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const num = value?.value ?? 0;
  const unit = Object.values(DateUnit).includes(value?.unit as DateUnit)
    ? (value?.unit as DateUnit)
    : DateUnit.DAYS;
  const direction = value?.direction ?? RelativeDateDirection.BEFORE;

  const handleNumChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const n = parseInt(e.target.value, 10);
    if (!Number.isNaN(n) && n >= 0) {
      onChange({
        type: 'relative',
        value: n,
        unit,
        direction,
      });
    }
  };

  const handleUnitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({
      type: 'relative',
      value: num,
      unit: e.target.value as DateUnit,
      direction,
    });
  };

  const handleDirectionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const d =
      e.target.value === RelativeDateDirection.AFTER
        ? RelativeDateDirection.AFTER
        : RelativeDateDirection.BEFORE;
    onChange({
      type: 'relative',
      value: num,
      unit,
      direction: d,
    });
  };

  return (
    <div className={styles.relativeDateRow}>
      <input
        type="number"
        min={0}
        value={num}
        onChange={handleNumChange}
        disabled={disabled}
        className={styles.textInput}
        aria-label="Relative date amount"
      />
      <select
        className={styles.selectInput}
        value={unit}
        onChange={handleUnitChange}
        disabled={disabled}
        aria-label="Relative date unit">
        <option value={DateUnit.DAYS}>days</option>
        <option value={DateUnit.MONTHS}>months</option>
        <option value={DateUnit.YEARS}>years</option>
      </select>
      <select
        className={styles.selectInput}
        value={direction}
        onChange={handleDirectionChange}
        disabled={disabled}
        aria-label="Before or after today">
        <option value={RelativeDateDirection.BEFORE}>ago (before today)</option>
        <option value={RelativeDateDirection.AFTER}>from now (after today)</option>
      </select>
    </div>
  );
};
